// src/components/GeofenceZoneDialog.tsx - MOBILE RESPONSIVE
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Box,
  Typography,
  Slider,
  Alert,
  Grid,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  SelectChangeEvent,
  Chip,
  CircularProgress,
  useTheme,
  useMediaQuery,
  IconButton,
  Collapse,
} from '@mui/material';
import { DirectionsCar, Close as CloseIcon } from '@mui/icons-material';
import api from '../services/api';
import GeofenceMapEditor from './GeofenceMapEditor';
import { closeRing, openRing, polygonError, validZonePoint, ZonePoint } from '../utils/geofenceGeometry';

interface Vehicle {
  _id: string;
  plateNumber: string;
  make: string;
  model: string;
  geofence?: {
    enabled: boolean;
  };
}

interface GeofenceZoneDialogProps {
  open: boolean;
  onClose: () => void;
  vehicleId?: string;
  zoneData?: any;
  onSuccess: () => void;
}

const GeofenceZoneDialog: React.FC<GeofenceZoneDialogProps> = ({
  open,
  onClose,
  vehicleId,
  zoneData,
  onSuccess
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [formData, setFormData] = useState({
    name: zoneData?.name || '',
    type: zoneData?.type || 'circular',
    radius: zoneData?.radius || 500,
    color: zoneData?.color || '#3b82f6',
    alertOnEntry: zoneData?.rules?.alertOnEntry ?? true,
    alertOnExit: zoneData?.rules?.alertOnExit ?? true,
    alertOnBreach: zoneData?.rules?.alertOnBreach ?? false,
    speedLimit: zoneData?.rules?.speedLimit || 60,
    isActive: zoneData?.isActive ?? true,
    latitude: zoneData?.center?.coordinates?.[1] ?? 6.5244,
    longitude: zoneData?.center?.coordinates?.[0] ?? 3.3792,
    scheduleEnabled: zoneData?.activeSchedule?.enabled || false,
    scheduleDays: zoneData?.activeSchedule?.days || [1, 2, 3, 4, 5],
    scheduleStartTime: zoneData?.activeSchedule?.startTime || '08:00',
    scheduleEndTime: zoneData?.activeSchedule?.endTime || '18:00',
  });

  const [boundaryPoints, setBoundaryPoints] = useState<ZonePoint[]>([]);
  const [hasCenter, setHasCenter] = useState(false);
  const [showCoordinates, setShowCoordinates] = useState(false);

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedVehicles, setSelectedVehicles] = useState<string[]>(
    vehicleId ? [vehicleId] : []
  );
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && !vehicleId) {
      loadVehicles();
    }
  }, [open, vehicleId]);


  // ✅ ADD THIS: Reset form data when zoneData changes
useEffect(() => {
  setBoundaryPoints(openRing(zoneData?.polygon?.coordinates?.[0] || []));
  setHasCenter(!!zoneData?.center?.coordinates && validZonePoint(zoneData.center.coordinates));
  setSelectedVehicles(vehicleId ? [vehicleId] : []);
  setShowCoordinates(false);
  setError(null);
  if (zoneData) {
    console.log('📝 Loading zone data for editing:', zoneData);
    setFormData({
      name: zoneData.name || '',
      type: zoneData.type || 'circular',
      radius: zoneData.radius || 500,
      color: zoneData.color || '#3b82f6',
      alertOnEntry: zoneData.rules?.alertOnEntry ?? true,
      alertOnExit: zoneData.rules?.alertOnExit ?? true,
      alertOnBreach: zoneData.rules?.alertOnBreach ?? false,
      speedLimit: zoneData.rules?.speedLimit || 60,
      isActive: zoneData.isActive ?? true,
      latitude: zoneData.center?.coordinates?.[1] ?? 6.5244,
      longitude: zoneData.center?.coordinates?.[0] ?? 3.3792,
      scheduleEnabled: zoneData.activeSchedule?.enabled || false,
      scheduleDays: zoneData.activeSchedule?.days || [1, 2, 3, 4, 5],
      scheduleStartTime: zoneData.activeSchedule?.startTime || '08:00',
      scheduleEndTime: zoneData.activeSchedule?.endTime || '18:00',
    });
  } else {
    // Reset to defaults when creating new zone
    setFormData({
      name: '',
      type: 'circular',
      radius: 500,
      color: '#3b82f6',
      alertOnEntry: true,
      alertOnExit: true,
      alertOnBreach: false,
      speedLimit: 60,
      isActive: true,
      latitude: 6.5244,
      longitude: 3.3792,
      scheduleEnabled: false,
      scheduleDays: [1, 2, 3, 4, 5],
      scheduleStartTime: '08:00',
      scheduleEndTime: '18:00',
    });
  }
}, [zoneData, open, vehicleId]); // ← Re-run when zoneData or dialog open state changes

  const loadVehicles = async () => {
    setLoadingVehicles(true);
    try {
      const response = await api.get('/vehicles');
      const allVehicles = response.data.data || [];
      setVehicles(allVehicles);
    } catch (err: any) {
      setError('Failed to load vehicles');
    } finally {
      setLoadingVehicles(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    if ((field === 'latitude' || field === 'longitude') && formData.type === 'circular') setHasCenter(true);
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDaysChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      scheduleDays: typeof value === 'string' ? [] : value,
    }));
  };

  const handleVehicleSelection = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedVehicles(typeof value === 'string' ? [] : value);
  };

  const geometryError = formData.type === 'polygon'
    ? polygonError(boundaryPoints)
    : !hasCenter ? 'Place a pin on the map to select your geofence location.'
    : !validZonePoint([formData.longitude, formData.latitude]) ? 'Enter a valid latitude and longitude.'
    : !Number.isFinite(formData.radius) || formData.radius < 50 || formData.radius > 50000 ? 'Radius must be between 50 and 50,000 meters.' : null;

  const handleSubmit = async () => {
    if (geometryError) { setError(geometryError); return; }
    if (!formData.name.trim()) {
      setError('Zone name is required');
      return;
    }
  
    if (!vehicleId && selectedVehicles.length === 0) {
      setError('Please select at least one vehicle');
      return;
    }
  
    setLoading(true);
    setError(null);
  
    try {
      const payload = {
        name: formData.name,
        type: formData.type,
        color: formData.color,
        isActive: formData.isActive,
        rules: {
          alertOnEntry: formData.alertOnEntry,
          alertOnExit: formData.alertOnExit,
          alertOnBreach: formData.alertOnBreach,
          speedLimit: formData.speedLimit,
          // ✅ ADD: Include all rule fields for compatibility
          allowEntry: !formData.alertOnBreach,
          allowExit: true,
          alertOnSpeedViolation: true
        },
        activeSchedule: formData.scheduleEnabled ? {
          enabled: true,
          days: formData.scheduleDays,
          startTime: formData.scheduleStartTime,
          endTime: formData.scheduleEndTime,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        } : { enabled: false },
        // ✅ FIXED: Always include geometry, regardless of edit mode
        ...(formData.type === 'circular' && {
          center: {
            type: 'Point',
            coordinates: [formData.longitude, formData.latitude],
          },
          radius: formData.radius,
        }),
        ...(formData.type === 'polygon' && {
          polygon: {
            type: 'Polygon',
            coordinates: [closeRing(boundaryPoints)],
          }
        }),
      };
  
      console.log('💾 Saving zone:', { vehicleId, zoneId: zoneData?._id, payload });
  
      if (vehicleId) {
        if (zoneData?._id) {
          // ✅ EDITING existing zone
          console.log('📝 Updating zone:', zoneData._id);
          const response = await api.put(
            `/geofencing/vehicles/${vehicleId}/zones/${zoneData._id}`,
            payload
          );
          console.log('✅ Zone updated:', response.data);
          alert(`Zone "${formData.name}" updated successfully!`);
        } else {
          // ✅ CREATING new zone
          console.log('➕ Creating new zone');
          const response = await api.post(
            `/geofencing/vehicles/${vehicleId}/zones`, 
            payload
          );
          console.log('✅ Zone created:', response.data);
          alert(`Zone "${formData.name}" created successfully!`);
        }
      } else {
        // ✅ Bulk apply to multiple vehicles
        console.log('📦 Bulk applying to vehicles:', selectedVehicles);
        await api.post('/geofencing/zones/bulk-apply', {
          vehicleIds: selectedVehicles,
          zoneData: payload
        });
        alert(`Zone "${formData.name}" applied to ${selectedVehicles.length} vehicles!`);
      }
  
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('❌ Zone save error:', err.response?.data || err);
      setError(err.response?.data?.message || 'Failed to save zone');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          maxHeight: isMobile ? '100%' : '90vh',
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant={isMobile ? 'h6' : 'h5'}>
            {zoneData ? 'Edit' : 'Create'} Geofence Zone
          </Typography>
          {isMobile && (
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          {/* Vehicle Selection */}
          {!vehicleId && (
            <Box sx={{ mb: 3, p: isMobile ? 1.5 : 2, bgcolor: 'rgba(59, 130, 246, 0.05)', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <DirectionsCar fontSize="small" /> Assign to Vehicles
              </Typography>
              
              {loadingVehicles ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <>
                  <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                    <InputLabel>Select Vehicles</InputLabel>
                    <Select
                      multiple
                      value={selectedVehicles}
                      onChange={handleVehicleSelection}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((vehicleId) => {
                            const vehicle = vehicles.find(v => v._id === vehicleId);
                            return (
                              <Chip
                                key={vehicleId}
                                label={vehicle?.plateNumber || vehicleId}
                                size="small"
                                color={vehicle?.geofence?.enabled ? 'primary' : 'default'}
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {vehicles.map((vehicle) => (
                        <MenuItem key={vehicle._id} value={vehicle._id}>
                          <Checkbox checked={selectedVehicles.includes(vehicle._id)} />
                          <ListItemText 
                            primary={`${vehicle.plateNumber} - ${vehicle.make} ${vehicle.model}`}
                            secondary={vehicle.geofence?.enabled ? 'Geofencing enabled' : 'Geofencing disabled'}
                            primaryTypographyProps={{ variant: isMobile ? 'body2' : 'body1' }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {selectedVehicles.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Selected {selectedVehicles.length} vehicle{selectedVehicles.length !== 1 ? 's' : ''}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          )}

          {/* Zone Name */}
          <TextField
            fullWidth
            label="Zone Name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="e.g., Office Parking, Warehouse Area"
            sx={{ mb: 3 }}
            size={isMobile ? 'small' : 'medium'}
            required
          />

          {/* Zone Type */}
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend">
              <Typography variant={isMobile ? 'caption' : 'body2'}>Zone Type</Typography>
            </FormLabel>
            <RadioGroup
              value={formData.type}
              onChange={(e) => handleChange('type', e.target.value)}
            >
              <FormControlLabel
                value="circular"
                control={<Radio size={isMobile ? 'small' : 'medium'} />}
                label={<Typography variant={isMobile ? 'caption' : 'body2'}>Circular (center point + radius)</Typography>}
              />
              <FormControlLabel
                value="polygon"
                control={<Radio size={isMobile ? 'small' : 'medium'} />}
                label={<Typography variant={isMobile ? 'caption' : 'body2'}>Polygon (custom boundaries)</Typography>}
              />
            </RadioGroup>
          </FormControl>

          <GeofenceMapEditor
            open={open}
            type={formData.type}
            center={[formData.longitude, formData.latitude]}
            radius={formData.radius}
            color={formData.color}
            hasCenter={hasCenter}
            points={boundaryPoints}
            onCenterChange={([longitude, latitude]) => {
              setFormData(prev => ({ ...prev, longitude, latitude }));
              setHasCenter(true);
            }}
            onPointsChange={setBoundaryPoints}
          />
          <Button size="small" aria-expanded={showCoordinates} onClick={() => setShowCoordinates(value => !value)} sx={{ mb: 2 }}>
            {showCoordinates ? 'Hide advanced coordinates' : 'Enter coordinates manually (advanced)'}
          </Button>
          <Collapse in={showCoordinates}>
          {/* Coordinates */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label={formData.type === 'polygon' ? 'Point latitude' : 'Latitude'}
                value={formData.latitude}
                onChange={(e) => handleChange('latitude', parseFloat(e.target.value))}
                inputProps={{ step: 0.000001 }}
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label={formData.type === 'polygon' ? 'Point longitude' : 'Longitude'}
                value={formData.longitude}
                onChange={(e) => handleChange('longitude', parseFloat(e.target.value))}
                inputProps={{ step: 0.000001 }}
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>
          </Grid>

            {formData.type === 'polygon' && <>
              <Button sx={{ mb: 2 }} disabled={!validZonePoint([formData.longitude, formData.latitude])} onClick={() => setBoundaryPoints(points => [...points, [formData.longitude, formData.latitude]])}>Add boundary point</Button>
              {boundaryPoints.map(([lng, lat], index) => <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="caption" sx={{ flexGrow: 1 }}>Point {index + 1}: {lat.toFixed(6)}, {lng.toFixed(6)}</Typography>
                <Button size="small" aria-label={`Remove boundary point ${index + 1}`} onClick={() => setBoundaryPoints(points => points.filter((_, i) => i !== index))}>Remove</Button>
              </Box>)}
            </>}
          </Collapse>

          {/* Radius */}
          {formData.type === 'circular' && (
            <Box sx={{ mb: 3 }}>
              <Typography variant={isMobile ? 'caption' : 'body2'} gutterBottom>
                Radius: {formData.radius} meters
              </Typography>
              <Slider
                aria-label="Geofence radius in meters"
                value={formData.radius}
                onChange={(_, value) => handleChange('radius', value)}
                min={50}
                max={5000}
                step={50}
                size={isMobile ? 'small' : 'medium'}
                marks={isMobile ? [
                  { value: 50, label: '50m' },
                  { value: 5000, label: '5km' },
                ] : [
                  { value: 50, label: '50m' },
                  { value: 500, label: '500m' },
                  { value: 1000, label: '1km' },
                  { value: 5000, label: '5km' },
                ]}
              />
            </Box>
          )}

          {/* Color Picker */}
          <Box sx={{ mb: 3 }}>
            <FormLabel>
              <Typography variant={isMobile ? 'caption' : 'body2'}>Zone Color</Typography>
            </FormLabel>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              {['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'].map(
                (color) => (
                  <Box
                    key={color}
                    onClick={() => handleChange('color', color)}
                    sx={{
                      width: isMobile ? 32 : 40,
                      height: isMobile ? 32 : 40,
                      backgroundColor: color,
                      borderRadius: 1,
                      cursor: 'pointer',
                      border: formData.color === color ? '3px solid #000' : '1px solid #ccc',
                    }}
                  />
                )
              )}
            </Box>
          </Box>

          {/* Alert Rules */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Alert Rules
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.alertOnEntry}
                  onChange={(e) => handleChange('alertOnEntry', e.target.checked)}
                  size={isMobile ? 'small' : 'medium'}
                />
              }
              label={<Typography variant={isMobile ? 'caption' : 'body2'}>Alert when vehicle enters zone</Typography>}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.alertOnExit}
                  onChange={(e) => handleChange('alertOnExit', e.target.checked)}
                  size={isMobile ? 'small' : 'medium'}
                />
              }
              label={<Typography variant={isMobile ? 'caption' : 'body2'}>Alert when vehicle exits zone</Typography>}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.alertOnBreach}
                  onChange={(e) => handleChange('alertOnBreach', e.target.checked)}
                  size={isMobile ? 'small' : 'medium'}
                />
              }
              label={<Typography variant={isMobile ? 'caption' : 'body2'}>Alert on unauthorized breach</Typography>}
            />
          </Box>

          {/* Speed Limit */}
          <Box sx={{ mb: 3 }}>
            <Typography variant={isMobile ? 'caption' : 'body2'} gutterBottom>
              Speed Limit: {formData.speedLimit} km/h
            </Typography>
            <Slider
              value={formData.speedLimit}
              onChange={(_, value) => handleChange('speedLimit', value)}
              min={20}
              max={120}
              step={5}
              size={isMobile ? 'small' : 'medium'}
              marks={isMobile ? [
                { value: 20, label: '20' },
                { value: 120, label: '120' },
              ] : [
                { value: 20, label: '20' },
                { value: 60, label: '60' },
                { value: 100, label: '100' },
                { value: 120, label: '120' },
              ]}
            />
          </Box>

          {/* Schedule */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Zone Schedule (Optional)
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.scheduleEnabled}
                  onChange={(e) => handleChange('scheduleEnabled', e.target.checked)}
                  size={isMobile ? 'small' : 'medium'}
                />
              }
              label={<Typography variant={isMobile ? 'caption' : 'body2'}>Enable time-based schedule</Typography>}
            />

            {formData.scheduleEnabled && (
              <>
                <FormControl fullWidth sx={{ mb: 2, mt: 2 }} size={isMobile ? 'small' : 'medium'}>
                  <InputLabel>Active Days</InputLabel>
                  <Select
                    multiple
                    value={formData.scheduleDays}
                    onChange={handleDaysChange}
                    renderValue={(selected: number[]) => {
                      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                      return selected.map((d: number) => dayNames[d]).join(', ');
                    }}
                  >
                    {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => (
                      <MenuItem key={index} value={index}>
                        <Checkbox checked={formData.scheduleDays.includes(index)} />
                        <ListItemText primary={day} primaryTypographyProps={{ variant: isMobile ? 'body2' : 'body1' }} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Start Time"
                      type="time"
                      value={formData.scheduleStartTime}
                      onChange={(e) => handleChange('scheduleStartTime', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size={isMobile ? 'small' : 'medium'}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="End Time"
                      type="time"
                      value={formData.scheduleEndTime}
                      onChange={(e) => handleChange('scheduleEndTime', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size={isMobile ? 'small' : 'medium'}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </Box>

          {/* Active Status */}
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => handleChange('isActive', e.target.checked)}
                size={isMobile ? 'small' : 'medium'}
              />
            }
            label={<Typography variant={isMobile ? 'caption' : 'body2'}>Zone is active</Typography>}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 0,
        p: 2,
        '& > button': {
          width: isMobile ? '100%' : 'auto'
        }
      }}>
        <Button onClick={onClose} disabled={loading} fullWidth={isMobile} variant={isMobile ? 'outlined' : 'text'}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !!geometryError || !formData.name.trim() || (!vehicleId && selectedVehicles.length === 0)}
          fullWidth={isMobile}
        >
          {loading ? 'Saving...' : zoneData ? 'Update Zone' : 
            vehicleId ? 'Create Zone' : `Create for ${selectedVehicles.length} Vehicle${selectedVehicles.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GeofenceZoneDialog;